/**
 * Hook for managing shares on a specific project (owner view).
 * Handles listing, creating, revoking, and updating shares.
 */

import { useState, useEffect, useCallback } from 'react';
import { sharingApi } from '@/services/sharingApi';
import type { Role, ShareRecord } from '@/lib/types';

interface UseProjectSharesReturn {
  shares: ShareRecord[];
  isLoading: boolean;
  error: string | null;
  shareProject: (email: string, role: Role) => Promise<{ isExistingUser: boolean }>;
  revokeShare: (email: string) => Promise<void>;
  updateRole: (email: string, role: Role) => Promise<void>;
  refresh: () => void;
}

export function useProjectShares(projectId: string | null): UseProjectSharesReturn {
  const [shares, setShares] = useState<ShareRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadShares = useCallback(async () => {
    if (!projectId) return;
    setIsLoading(true);
    setError(null);
    try {
      const result = await sharingApi.listProjectShares(projectId);
      setShares(result.shares);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load shares';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadShares();
  }, [loadShares]);

  const shareProject = useCallback(
    async (email: string, role: Role) => {
      if (!projectId) throw new Error('No project selected');
      setError(null);
      const result = await sharingApi.shareProject(projectId, email, role);
      await loadShares();
      return { isExistingUser: result.isExistingUser };
    },
    [projectId, loadShares]
  );

  const revokeShare = useCallback(
    async (email: string) => {
      if (!projectId) return;
      await sharingApi.revokeShare(projectId, email);
      await loadShares();
    },
    [projectId, loadShares]
  );

  const updateRole = useCallback(
    async (email: string, role: Role) => {
      if (!projectId) return;
      await sharingApi.updateShareRole(projectId, email, role);
      await loadShares();
    },
    [projectId, loadShares]
  );

  return {
    shares,
    isLoading,
    error,
    shareProject,
    revokeShare,
    updateRole,
    refresh: loadShares,
  };
}
